import Lenis from 'lenis';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import WindowSize from './WindowSize';

gsap.registerPlugin(ScrollTrigger);

let instance: Scroll;

class Scroll {
  scroll!: Lenis;
  scrollbarWidth!: number;
  hasScrollBarOnInit!: boolean;
  windowSize: WindowSize;

  constructor() {
    if (instance) {
      throw new Error('You can only create one instance!');
    }
    instance = this;

    this.hasScrollBarOnInit = true;
    this.windowSize = WindowSize.getInstance();
    this.scrollbarWidth = 0;

    this.init();
  }

  init() {
    // Initializes smooth scrolling with Lenis and integrates it with GSAP's ScrollTrigger.
    // Function to set up smooth scrolling.
    // Initialize Lenis for smooth scroll effects. Lerp value controls the smoothness.
    const lenis = new Lenis({
      lerp: 0.25,
      syncTouch: true,
      syncTouchLerp: 0.1,
      prevent: (node) =>
        node.id === 'header-nav' ||
        !!node.closest('.shopify-buy__cart-scroll') ||
        !!node.closest('.shopify-buy-cart-wrapper') ||
        !!node.closest('.cky-preference-body-wrapper') ||
        !!node.closest('[data-table-header-scroll]') ||
        !!node.closest('[data-table-body-scroll]'),
    });

    // Sync ScrollTrigger with Lenis' scroll updates.
    lenis.on('scroll', ScrollTrigger.update);

    // Ensure GSAP animations are in sync with Lenis' scroll frame updates.
    gsap.ticker.add((time) => {
      lenis.raf(time * 1000); // Convert GSAP's time to milliseconds for Lenis.
    });

    // Turn off GSAP's default lag smoothing to avoid conflicts with Lenis.
    gsap.ticker.lagSmoothing(0);

    this.scroll = lenis;

    this.onResize(); // Initial check
    this.windowSize.addListener(this.onResize.bind(this));
  }

  onResize() {
    this.getScrollbarWidth();
    this.checkIfPageHasScrollbar();
  }

  refresh() {
    if (typeof (this.scroll as Lenis & { resize?: () => void }).resize === 'function') {
      (this.scroll as Lenis & { resize: () => void }).resize();
    }

    this.onResize();
    ScrollTrigger.refresh();
    ScrollTrigger.update();
  }

  checkIfPageHasScrollbar() {
    this.hasScrollBarOnInit = document.body.scrollHeight > window.innerHeight;
  }

  disableScrolling() {
    this.scroll.stop();

    // Apply padding to the body to compensate for the removed scrollbar
    document.documentElement.style.setProperty(
      '--scrollbar-width',
      `${this.hasScrollBarOnInit ? this.scrollbarWidth : 0}px`,
    );
  }

  enableScrolling() {
    this.scroll.start();

    // Remove the padding from the body when scrolling is re-enabled
    document.documentElement.style.setProperty('--scrollbar-width', `0px`);
  }

  getScrollbarWidth() {
    // Create a temporary div container and append it into the body
    const container = document.createElement('div');
    // Append the element into the body
    document.body.appendChild(container);
    // Force scrollbar on the element
    container.style.overflow = 'scroll';

    // Add ad fake div inside the container
    const inner = document.createElement('div');
    container.appendChild(inner);

    // Calculate the width based on the container width minus its child width
    const scrollbarWidth = container.offsetWidth - inner.offsetWidth;

    // Remove the temporary elements from the DOM
    container.parentNode?.removeChild(container);

    this.scrollbarWidth = scrollbarWidth;
  }

  scrollToSection($target: JQuery<HTMLElement>, centerInView = false) {
    const offset = $target.offset();
    if (!offset) return;
    let topOffset = offset.top;

    if (centerInView) {
      // element size
      const height = $target.outerHeight() || 0;
      // viewport size
      const windowHeight = window.innerHeight;
      // calculate center position
      topOffset = topOffset - windowHeight / 2 + height / 2;
    }

    this.scroll.scrollTo(topOffset, {
      offset: centerInView ? 0 : -150,
      duration: 1.5,
    });
  }
}

const SmoothScroll = new Scroll();
// allow accessing through the window
(window as any).SmoothScroll = SmoothScroll.scroll;

export default SmoothScroll;
